import { FC, ReactNode } from 'react';
import { BLOCKS } from '@contentful/rich-text-types';
import { stringOrRichTextToString } from '@belong/contentful';
import { COLOURS, FLAIRS } from '@belong/themes';
import {
  CorePalette,
  FlairsKey,
  FlairsValue,
  ICalloutProps,
  ICalloutStyleProps,
  IGetCalloutStyles
} from '@belong/types';
import { CALLOUT as TEST_ID } from '../../../helpers/testIds';
import { Icon } from '../../Icon';
import { RichText } from '../../styles/Typography';
import { Column, Columns } from '../../ui';
import { CalloutContainer, CalloutDescription, CalloutTitle } from './Callout.styles';

const getCalloutStyles = ({ variant, isSolid }: ICalloutStyleProps) => {
  const calloutStylesMap: { [key in CorePalette]: IGetCalloutStyles } = {
    PRIMARY: {
      backgroundColor: isSolid ? 'LIGHT_BLUE' : 'WHITE',
      borderColor: isSolid ? 'LIGHT_BLUE' : 'BELONG_BLUE_DARK'
    },
    ACCENT1: {
      backgroundColor: isSolid ? 'DISCO_PINK' : 'WHITE',
      borderColor: isSolid ? 'DISCO_PINK' : 'DISCO_PINK_DARK'
    },
    ACCENT2: {
      backgroundColor: isSolid ? 'PEPPERMINT_CRISP' : 'WHITE',
      borderColor: isSolid ? 'PEPPERMINT_CRISP' : 'PEPPERMINT_CRISP_DARK'
    },
    ACCENT3: {
      backgroundColor: isSolid ? 'PASSION_POP' : 'WHITE',
      borderColor: isSolid ? 'PASSION_POP' : 'PASSION_POP_DARK'
    },
    NEUTRAL_LIGHT: {
      backgroundColor: isSolid ? 'GREY_200' : 'WHITE',
      borderColor: isSolid ? 'GREY_200' : 'GREY_500'
    }
  };

  return calloutStylesMap[variant];
};

const renderCalloutTitle = (fontColor: string) => ({
  // eslint-disable-next-line react/display-name
  [BLOCKS.PARAGRAPH]: (_, children: ReactNode) => (
    <CalloutTitle data-testid={TEST_ID.TITLE} as="span" hasColor={fontColor} isBold>
      {children}
    </CalloutTitle>
  )
});

const renderCalloutDescription = (fontColor: string) => ({
  // eslint-disable-next-line react/display-name
  [BLOCKS.PARAGRAPH]: (_, children: ReactNode) => (
    <CalloutDescription data-testid={TEST_ID.DESCRIPTION} hasColor={fontColor} variant="small">
      {children}
    </CalloutDescription>
  )
});

export const TITLE_COLOUR = {
  DEFAULT: COLOURS.GREY_700,
  BLACK: COLOURS.BELONG_BLUE_LIGHT
};

export const DESCRIPTION_COLOUR = {
  DEFAULT: COLOURS.GREY_600,
  BLACK: COLOURS.BELONG_BLUE_LIGHT
};

/**
 * Callouts are molecules used inside components, such as the Plan Card or Primary Banner to emphasise product promotions.
 */
export const Callout: FC<ICalloutProps> = ({
  colour = 'PASSION_POP',
  isSolid = true,
  icon,
  title,
  description,
  extendedDictionary,
  fullwidth = false,
  ...elementProps
}) => {
  // determine if the colour belongs to the older FlairsKey or the new CorePalette
  const isFlair = Object.values(FLAIRS).includes(colour as FlairsKey);

  const resolvedBackground = isFlair
    ? (colour as FlairsValue)
    : getCalloutStyles({
        variant: colour as CorePalette,
        isSolid
      });

  const titleColor = colour === FLAIRS.BLACK ? TITLE_COLOUR.BLACK : TITLE_COLOUR.DEFAULT;
  const descriptionColor = colour === FLAIRS.BLACK ? DESCRIPTION_COLOUR.BLACK : DESCRIPTION_COLOUR.DEFAULT;

  let resolvedTitle = title;
  let resolvedDescription = description;

  // If there's a description but no title, use the description as the title
  // And undefined the description to avoid duplication
  if (
    !stringOrRichTextToString(
      typeof resolvedTitle === 'object' && resolvedTitle !== null && 'html' in resolvedTitle
        ? resolvedTitle.html
        : (resolvedTitle ?? '')
    ) &&
    description
  ) {
    resolvedTitle = description;
    resolvedDescription = undefined;
  }

  return (
    <CalloutContainer
      data-testid={TEST_ID.ROOT}
      background={resolvedBackground}
      isSolid={isSolid}
      isFullwidth={fullwidth}
      {...(elementProps || {})}
    >
      <Columns space="xsmall" alignX="left">
        {icon && (
          <Column width="content" alignX="center">
            <Icon data-testid={TEST_ID.ICON} name={icon.name} hasColor={titleColor} />
          </Column>
        )}

        <Column width="flex">
          {resolvedTitle && (
            <RichText {...resolvedTitle} dictionary={extendedDictionary} renderNode={renderCalloutTitle(titleColor)} />
          )}
          {resolvedDescription && (
            <RichText
              {...resolvedDescription}
              dictionary={extendedDictionary}
              renderNode={renderCalloutDescription(descriptionColor)}
            />
          )}
        </Column>
      </Columns>
    </CalloutContainer>
  );
};

Callout.displayName = 'Callout';

export default Callout;
